import Immutable from 'immutable';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import flatten from 'lodash/flatten';
import { v4 as uuidv4 } from 'uuid';

import {
  TEMP_CATEGORY_ID_PREFIX,
  TOP_LEVEL_CATEGORY_ID,
  TOP_LEVEL_CATEGORY_TITLE_BY_ID
} from 'app/constants/AdminBrowseConstants';
import { isValidUrl } from 'app/helpers/admin/browse';

export const addCategory = (categoryConfig, parentCategoryId, newCategory) => {
  const configCopy = cloneDeep(categoryConfig);

  const { categoryId: newCategoryId } = newCategory;

  const { categoryById } = configCopy;
  const parentCategory = categoryById[parentCategoryId];
  if (!parentCategory) {
    throw new Error(`No parent category found with categoryId: ${parentCategoryId}`);
  }

  categoryById[newCategoryId] = newCategory;
  parentCategory.subCategoryIds.push(newCategoryId);

  return configCopy;
};

export const deleteCategoryFromConfig = (categoryId, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryById = configCopy.categoryById;
  const categoryToDelete = categoryById[categoryId];
  if (!categoryToDelete) {
    throw new Error(`${categoryId} not found in config`);
  }

  const parentCategory = Object.values(categoryById).find(category => {
    const { subCategoryIds = [] } = category;

    return subCategoryIds.indexOf(categoryId) > -1;
  });
  if (!parentCategory) {
    throw new Error(`No parent category found for categoryId: ${categoryId}`);
  }

  const { categoryId: parentCategoryId, subCategoryIds: parentSubCategoryIds = [] } = parentCategory;
  if (!parentCategoryId) {
    throw new Error('Parent category is missing a categoryId');
  }

  // remove child categories of the catagory to delete
  const { subCategoryIds = [] } = categoryToDelete;
  subCategoryIds.forEach(childCategoryId => {
    delete categoryById[childCategoryId];
  });

  const indexOfChildCategoryId = parentSubCategoryIds.indexOf(categoryId);
  const newSubCategoryIds = parentSubCategoryIds
    .slice(0, indexOfChildCategoryId)
    .concat(parentSubCategoryIds.slice(indexOfChildCategoryId + 1));

  categoryById[parentCategoryId].subCategoryIds = newSubCategoryIds;

  delete categoryById[categoryId];

  return configCopy;
};

export const reOrderSubCategoryIds = (parentCategoryId, newSubCategoryIds, categoryConfig) => {
  const configCopy = cloneDeep(categoryConfig);

  const categoryById = configCopy.categoryById;
  const parentCategory = categoryById[parentCategoryId];

  parentCategory.subCategoryIds = newSubCategoryIds;

  return configCopy;
};

export const getSubCategories = (categoryId, categoryConfig) => {
  if (!categoryConfig) {
    return [];
  }

  const { categoryById } = categoryConfig;
  const parentCategory = categoryById[categoryId];
  if (!parentCategory) {
    return [];
  }

  return parentCategory.subCategoryIds.map(subCategoryId => categoryById[subCategoryId]);
};

export const hasAdminPermissions = user => {
  if (!user) {
    return false;
  }

  if (!Immutable.Map.isMap(user)) {
    throw new Error('user must be an Immutable.Map');
  }

  return user.get('permissions').indexOf('BROWSE_CATEGORY_MANAGER') > -1;
};

export const isTopLevelCategoryId = topLevelCategoryId => {
  return Object.values(TOP_LEVEL_CATEGORY_ID).includes(topLevelCategoryId);
};

export const isUnsavedCategory = category => {
  return category.categoryId.startsWith(TEMP_CATEGORY_ID_PREFIX);
};

export const makeCategoryConfigId = (subdomain, localeCode, topLevelCategoryId) => {
  const categoryId = topLevelCategoryId.substring(1); // remove initial `/`
  return `${subdomain}:${localeCode}:${categoryId}`;
};

export const makeCategorySlug = categoryTitle => {
  // remove accents, symbols and punctuation
  let slug = categoryTitle
    .normalize('NFKD') // decompose accented chars
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/[^a-zA-Z0-9]+/g, '-') // replace non-alphanumeric with dashes
    .replace(/^-+|-+$/g, '') // trim leading/trailing dashes
    .toLowerCase();

  // if the result is empty, URI-encode it and remove invalid characters
  if (!slug) {
    slug = encodeURIComponent(categoryTitle)
      .replace(/%/g, '')
      .replace(/\+/g, '-') // URL-safe
      .replace(/\//g, '_')
      .replace(/=+$/, '')
      .toLowerCase();
  }

  return slug;
};

export const makeEmptyCategory = (categoryId = null, { isEnabled = false, title = '' } = {}) => {
  return {
    categoryId: categoryId || uuidv4(),
    isEnabled: isEnabled || false,
    searchUrl: '',
    slug: makeCategorySlug(title),
    subCategoryIds: [],
    title
  };
};

export const makeEmptyCategoryConfig = (subdomain, localeCode, topLevelCategoryId) => {
  const categoryConfigId = null;
  const title = TOP_LEVEL_CATEGORY_TITLE_BY_ID?.[topLevelCategoryId] ?? '';

  return {
    categoryConfigId,
    categoryConfigSchemaVersion: '1.0.0',
    categoryById: {
      [topLevelCategoryId]: makeEmptyCategory(topLevelCategoryId, { isEnabled: true, title })
    },
    localeCode,
    subdomain,
    topLevelCategoryId,
    updatedAt: Date.now(),
    updatedBy: ''
  };
};

export const isValidCategorySlug = slugCandidate => {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugCandidate);
};

const getReachableCategoryIdsRecursive = (categoryId, categoryById, depth = 0) => {
  if (depth === 10) {
    throw new Error('max depth of recursive function exceeded');
  }

  const category = categoryById[categoryId];
  const { subCategoryIds } = category;

  const recursiveSubCategoryIds = flatten(
    subCategoryIds.map(subCategoryId => {
      return getReachableCategoryIdsRecursive(subCategoryId, categoryById, depth + 1);
    })
  );

  return [categoryId].concat(recursiveSubCategoryIds);
};

export const serializeCategoryConfig = (categoryConfig, { updatedAt = 0, updatedBy = '' } = {}) => {
  const serializedCategoryConfig = cloneDeep(categoryConfig);

  serializedCategoryConfig.updatedAt = updatedAt || Date.now();
  serializedCategoryConfig.updatedBy = updatedBy;

  // ensure there are no orphaned categories
  const { categoryById, topLevelCategoryId } = serializedCategoryConfig;
  const allReachableCategoryIds = getReachableCategoryIdsRecursive(topLevelCategoryId, categoryById, 0);
  const allOrphanedCategoryIds = difference(Object.keys(categoryById), allReachableCategoryIds);

  allOrphanedCategoryIds.forEach(orphanedCategoryId => {
    delete serializedCategoryConfig.categoryById[orphanedCategoryId];
  });

  return serializedCategoryConfig;
};

export const validateCategory = (categoryCandidate, { siblingCategories = [] } = {}) => {
  const { categoryId, searchUrl, slug, title } = categoryCandidate;

  const isValidSearchUrl = isValidUrl(searchUrl);
  const isValidTitle = Boolean(title);

  const isValidSlug = isValidCategorySlug(slug);
  const isUniqueSlug =
    isValidSlug &&
    !siblingCategories.some(siblingCategory => {
      const { categoryId: siblingCategoryId, slug: siblingSlug } = siblingCategory;
      if (siblingCategoryId === categoryId) {
        return false;
      }

      return slug === siblingSlug;
    });
  let slugError = '';
  if (!isValidSlug) {
    slugError = 'A valid slug is required';
  } else if (!isUniqueSlug) {
    slugError = 'URL slug already exists. Add a different URL slug.';
  }

  const isValid = isValidSearchUrl && isValidSlug && isUniqueSlug && isValidTitle;

  return {
    isValid,
    errorByAttribute: {
      searchUrl: isValidSearchUrl ? '' : 'A catalog search URL is required',
      slug: slugError,
      title: isValidTitle ? '' : 'A title is required'
    }
  };
};
